import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { Text, PrimaryButton, DefaultButton, ActionButton, Dropdown, IDropdownOption, TextField } from '@fluentui/react';
import type { SpawnRequest, ToolSelection, EnvironmentConfig } from '../types';
import { getDefaultToolSelection, getSelectedToolNames } from '../types/tools';
import { api } from '../services/api';
import { fetchWorkspaces, getWorkspaceUrls, type Workspace } from '../types/workspace';
import ToolSelector from './ToolSelector';
import EnvironmentPicker from './EnvironmentPicker';
import styles from './SpawnForm.module.css';

const STEPS = [
  { key: 'workspace', label: 'Workspace' },
  { key: 'tools', label: 'Tools' },
  { key: 'connect', label: 'Connect' },
  { key: 'environments', label: 'Environments' },
  { key: 'sharepoint', label: 'SharePoint' },
  { key: 'spawn', label: 'Spawn' },
] as const;

interface SpawnFormProps {
  onSubmit: (req: SpawnRequest) => void;
  isSubmitting: boolean;
}

function useOAuthPopup(getUrl: () => string, messageType: string, storagePrefix: string) {
  const [token, setToken] = useState<string | null>(() => sessionStorage.getItem(`agentbox:${storagePrefix}-token`));
  const [username, setUsername] = useState<string | null>(() => sessionStorage.getItem(`agentbox:${storagePrefix}-username`));
  const [loading, setLoading] = useState(false);

  const handleMessage = useCallback((event: MessageEvent) => {
    if (event.data?.type === messageType && event.data.token) {
      setToken(event.data.token);
      setUsername(event.data.username || null);
      sessionStorage.setItem(`agentbox:${storagePrefix}-token`, event.data.token);
      if (event.data.username) sessionStorage.setItem(`agentbox:${storagePrefix}-username`, event.data.username);
      setLoading(false);
    } else if (event.data?.type === `${messageType}-error`) {
      setLoading(false);
    }
  }, [messageType, storagePrefix]);

  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleMessage]);

  function connect() {
    setLoading(true);
    const width = 600, height = 700;
    const left = window.screenX + (window.innerWidth - width) / 2;
    const top = window.screenY + (window.innerHeight - height) / 2;
    const popup = window.open(getUrl(), `${storagePrefix}-oauth`, `width=${width},height=${height},left=${left},top=${top},popup=yes`);
    if (popup) {
      const timer = setInterval(() => { if (popup.closed) { clearInterval(timer); setLoading(false); } }, 500);
    } else { setLoading(false); }
  }

  function disconnect() {
    setToken(null);
    setUsername(null);
    sessionStorage.removeItem(`agentbox:${storagePrefix}-token`);
    sessionStorage.removeItem(`agentbox:${storagePrefix}-username`);
  }

  return { token, username, loading, connected: !!token, connect, disconnect };
}

export default function SpawnForm({ onSubmit, isSubmitting }: SpawnFormProps) {
  const [searchParams] = useSearchParams();
  const preselectedWorkspace = searchParams.get('workspace');
  const { accounts } = useMsal();
  const userEmail = accounts[0]?.username || '';

  const [step, setStep] = useState(preselectedWorkspace ? 1 : 0);
  const [selectedWorkspace, setSelectedWorkspace] = useState<string | undefined>(preselectedWorkspace || undefined);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);

  const github = useOAuthPopup(() => api.getGitHubOAuthUrl(), 'github-oauth', 'github');
  const ado = useOAuthPopup(() => api.getAdoOAuthUrl(), 'ado-oauth', 'ado');
  const jira = useOAuthPopup(() => api.getAtlassianOAuthUrl(), 'atlassian-oauth', 'atlassian');

  // Environments (replaces manual Dataverse config)
  const [environmentConfigs, setEnvironmentConfigs] = useState<EnvironmentConfig[]>([]);

  // SharePoint — prepopulate with workspace team site
  const [spSites, setSpSites] = useState<string[]>([]);
  const [spSiteInput, setSpSiteInput] = useState('');

  // Auto-add workspace SharePoint site when workspace changes
  useEffect(() => {
    if (!selectedWorkspace) return;
    const wsUrl = `https://thenetworg.sharepoint.com/teams/${selectedWorkspace}`;
    setSpSites((prev) => prev.includes(wsUrl) ? prev : [wsUrl, ...prev.filter((s) => !s.startsWith('https://thenetworg.sharepoint.com/teams/'))]);
  }, [selectedWorkspace]);

  // Tool selection
  const [toolSelection, setToolSelection] = useState<ToolSelection>(getDefaultToolSelection);
  useEffect(() => {
    fetchWorkspaces().then(setWorkspaces).catch(() => {});
  }, []);

  const workspaceOptions: IDropdownOption[] = workspaces.map((w) => ({ key: w.code, text: `${w.code} — ${w.name}` }));
  const selectedWs = workspaces.find((w) => w.code === selectedWorkspace);
  const selectedUrls = selectedWs ? getWorkspaceUrls(selectedWs.code) : null;

  function addSpSite() {
    if (!spSiteInput.trim()) return;
    setSpSites((prev) => [...prev, spSiteInput.trim()]);
    setSpSiteInput('');
  }

  function handleSpawn() {
    onSubmit({
      ...(github.token && { githubToken: github.token }),
      ...(ado.token && { adoToken: ado.token }),
      ...(jira.token && { jiraToken: jira.token }),
      ...(environmentConfigs.length > 0 && { environments: environmentConfigs }),
      ...(spSites.length > 0 && { sharePointSites: spSites }),
    });
  }

  const canProceedFromWorkspace = !!selectedWorkspace;
  const canSpawn = canProceedFromWorkspace && github.connected;

  return (
    <div className={styles.container}>
      <nav className={styles.steps}>
        {STEPS.map((s, i) => (
          <button
            key={s.key}
            className={`${styles.stepBtn} ${i === step ? styles.active : ''} ${i < step ? styles.done : ''}`}
            onClick={() => setStep(i)}
            type="button"
          >
            <span className={styles.stepNum}>{i + 1}</span>
            <span className={styles.stepLabel}>{s.label}</span>
          </button>
        ))}
      </nav>

      <div className={styles.content}>
        {/* Step 0: Workspace */}
        {step === 0 && (
          <div className={styles.field}>
            <Text variant="medium" styles={{ root: { fontWeight: 600 } }}>Select Workspace</Text>
            <Text variant="small" styles={{ root: { color: 'var(--ab-text-secondary)' } }}>
              Choose which workspace this instance belongs to. The Azure DevOps repo will be cloned automatically.
            </Text>
            <Dropdown
              placeholder="Select a workspace…"
              options={workspaceOptions}
              selectedKey={selectedWorkspace}
              onChange={(_, option) => setSelectedWorkspace(option?.key as string)}
              styles={{ root: { width: 320 }, dropdown: { minWidth: 320 } }}
            />
            {selectedWs && selectedUrls && (
              <div style={{ marginTop: 8, padding: '10px 12px', background: 'var(--ab-bg-elevated)', borderRadius: 2, border: '1px solid var(--ab-border)' }}>
                <Text variant="small" styles={{ root: { color: 'var(--ab-text-secondary)', display: 'block' } }}>
                  <strong>Repo:</strong> {selectedUrls.repoUrl}
                </Text>
                <Text variant="small" styles={{ root: { color: 'var(--ab-text-secondary)', display: 'block', marginTop: 4 } }}>
                  <strong>ADO:</strong> {selectedUrls.adoProject}
                </Text>
                <Text variant="small" styles={{ root: { color: 'var(--ab-text-secondary)', display: 'block', marginTop: 4 } }}>
                  <strong>SharePoint:</strong> {selectedUrls.sharepointUrl}
                </Text>
              </div>
            )}
          </div>
        )}

        {/* Step 1: Tools */}
        {step === 1 && (
          <div className={styles.field}>
            <Text variant="medium" styles={{ root: { fontWeight: 600 } }}>Configure Tools</Text>
            <Text variant="small" styles={{ root: { color: 'var(--ab-text-secondary)' } }}>
              Select which tools your AgentBox should have access to. Tools that require authentication will be configured in the following steps.
            </Text>
            <ToolSelector value={toolSelection} onChange={setToolSelection} environmentCount={environmentConfigs.length} />
          </div>
        )}

        {/* Step 2: Connect (all OAuth providers) */}
        {step === 2 && (
          <div className={styles.field}>
            <Text variant="medium" styles={{ root: { fontWeight: 600 } }}>Connect Services</Text>
            <Text variant="small" styles={{ root: { color: 'var(--ab-text-secondary)' } }}>
              Authenticate with external services. GitHub is required; others are optional.
            </Text>
            <div className={styles.authGrid}>
              <OAuthCard
                title="GitHub"
                description="Required. Git operations & Copilot CLI."
                oauth={github}
                required
              />
              <OAuthCard
                title="Azure DevOps"
                description="Optional. Repo access & pipelines."
                oauth={ado}
              />
              <OAuthCard
                title="Jira"
                description="Optional. Issue tracking."
                oauth={jira}
              />
            </div>
          </div>
        )}

        {/* Step 3: Environments */}
        {step === 3 && (
          <div className={styles.field}>
            <Text variant="medium" styles={{ root: { fontWeight: 600 } }}>Environments</Text>
            <Text variant="small" styles={{ root: { color: 'var(--ab-text-secondary)' } }}>
              Select which Power Platform / Dataverse environments your AgentBox should have access to and the permission level for each.
            </Text>
            <EnvironmentPicker
              workspaceCode={selectedWorkspace}
              value={environmentConfigs}
              onChange={setEnvironmentConfigs}
              userEmail={userEmail}
              showLifecycle
            />
          </div>
        )}

        {/* Step 4: SharePoint */}
        {step === 4 && (
          <div className={styles.field}>
            <Text variant="medium" styles={{ root: { fontWeight: 600 } }}>SharePoint Sites</Text>
            <Text variant="small" styles={{ root: { color: 'var(--ab-text-secondary)' } }}>
              The workspace team site is included automatically. Add additional sites if needed.
            </Text>
            {spSites.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {spSites.map((site, i) => {
                  const isWorkspaceSite = selectedUrls && site === selectedUrls.sharepointUrl;
                  return (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: 'var(--ab-bg-elevated)', border: '1px solid var(--ab-border)', borderRadius: 2 }}>
                      <Text variant="small" styles={{ root: { flex: 1 } }}>
                        {site}
                        {isWorkspaceSite && (
                          <Text variant="tiny" styles={{ root: { color: 'var(--ab-text-subtle)', marginLeft: 8 } }}>workspace</Text>
                        )}
                      </Text>
                      {!isWorkspaceSite && (
                        <ActionButton
                          iconProps={{ iconName: 'Delete' }}
                          styles={{ root: { height: 'auto', padding: 0, color: 'var(--ab-error)' } }}
                          onClick={() => setSpSites((prev) => prev.filter((_, j) => j !== i))}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            <TextField
              label="Additional site URL"
              placeholder="https://thenetworg.sharepoint.com/sites/other-project"
              value={spSiteInput}
              onChange={(_, v) => setSpSiteInput(v || '')}
            />
            <DefaultButton text="+ Add Site" onClick={addSpSite} disabled={!spSiteInput.trim()} />
          </div>
        )}

        {/* Step 5: Spawn */}
        {step === 5 && (
          <div className={styles.field}>
            <Text variant="medium" styles={{ root: { fontWeight: 600 } }}>Ready to Spawn</Text>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <Text variant="small" styles={{ root: { color: 'var(--ab-text-secondary)' } }}>
                <strong>Workspace:</strong> {selectedWs?.name || 'None selected'}
              </Text>
              {(() => {
                const selectedNames = getSelectedToolNames(toolSelection);
                return selectedNames.length > 0 ? (
                  <Text variant="small" styles={{ root: { color: 'var(--ab-success)' } }}>
                    <strong>Tools:</strong> {selectedNames.join(', ')}
                  </Text>
                ) : (
                  <Text variant="small" styles={{ root: { color: 'var(--ab-text-secondary)' } }}>
                    <strong>Tools:</strong> None selected
                  </Text>
                );
              })()}
              <Text variant="small" styles={{ root: { color: github.connected ? 'var(--ab-success)' : 'var(--ab-error)' } }}>
                <strong>GitHub:</strong> {github.connected ? `Connected${github.username ? ` (${github.username})` : ''}` : 'Not connected'}
              </Text>
              {ado.connected && (
                <Text variant="small" styles={{ root: { color: 'var(--ab-success)' } }}>
                  <strong>Azure DevOps:</strong> Connected{ado.username ? ` (${ado.username})` : ''}
                </Text>
              )}
              {jira.connected && (
                <Text variant="small" styles={{ root: { color: 'var(--ab-success)' } }}>
                  <strong>Jira:</strong> Connected{jira.username ? ` (${jira.username})` : ''}
                </Text>
              )}
              {environmentConfigs.length > 0 && (
                <Text variant="small" styles={{ root: { color: 'var(--ab-success)' } }}>
                  <strong>Environments:</strong> {environmentConfigs.map((e) => `${e.friendlyName} (${e.accessLevel})`).join(', ')}
                </Text>
              )}
              {spSites.length > 0 && (
                <Text variant="small" styles={{ root: { color: 'var(--ab-success)' } }}>
                  <strong>SharePoint:</strong> {spSites.length} site{spSites.length !== 1 ? 's' : ''}
                </Text>
              )}
            </div>
            <PrimaryButton
              text={isSubmitting ? 'Spawning…' : !canSpawn ? '🔒 Complete previous steps' : '🚀 Spawn AgentBox'}
              onClick={handleSpawn}
              disabled={isSubmitting || !canSpawn}
              styles={{ root: { marginTop: 8 } }}
            />
          </div>
        )}
      </div>

      <div className={styles.nav}>
        {step > 0 && (
          <DefaultButton text="← Back" onClick={() => setStep((s) => s - 1)} />
        )}
        <div className={styles.navRight}>
          {step < STEPS.length - 1 && (
            <PrimaryButton
              text="Next →"
              onClick={() => setStep((s) => s + 1)}
              disabled={step === 0 && !canProceedFromWorkspace}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function OAuthCard({ title, description, oauth, required }: {
  title: string;
  description: string;
  oauth: ReturnType<typeof useOAuthPopup>;
  required?: boolean;
}) {
  return (
    <div className={`${styles.authCard} ${oauth.connected ? styles.authCardConnected : ''}`}>
      <div className={styles.authCardInfo}>
        <Text variant="small" styles={{ root: { fontWeight: 600 } }}>
          {title}
          {required && !oauth.connected && <span style={{ color: 'var(--ab-error)', marginLeft: 4 }}>*</span>}
        </Text>
        <Text variant="tiny" styles={{ root: { color: 'var(--ab-text-secondary)' } }}>{description}</Text>
      </div>
      <div className={styles.authCardAction}>
        {oauth.connected ? (
          <>
            <Text variant="tiny" styles={{ root: { color: 'var(--ab-success)', fontWeight: 500 } }}>
              ✓ {oauth.username || 'Connected'}
            </Text>
            <ActionButton
              text="Disconnect"
              onClick={oauth.disconnect}
              styles={{ root: { height: 'auto', padding: 0, fontSize: 11, color: 'var(--ab-text-subtle)' } }}
            />
          </>
        ) : (
          <DefaultButton
            text={oauth.loading ? 'Connecting…' : `Connect ${title}`}
            onClick={oauth.connect}
            disabled={oauth.loading}
            styles={{ root: { minWidth: 0, fontSize: 12, padding: '4px 12px', height: 28 } }}
          />
        )}
      </div>
    </div>
  );
}
